/**
 * Preview Step Component
 * 
 * Final step in Auto Fleet Generator - displays generated fleet preview
 * with timeline visualization and summary statistics
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Divider,
} from '@mui/material';

import { PreviewStepProps } from './types';
import FleetSummaryCards from './FleetSummaryCards';
import FleetTimelineVisualization from './FleetTimelineVisualization';
import FixedRouteTimetableVisualization from './FixedRouteTimetableVisualization';
import MixedServiceTimetableVisualization from './MixedServiceTimetableVisualization';
import { parseTime } from './timeUtils';
import { logEvent } from '../../../../services/analytics';

import { Icon } from '@liftango/ui-icons';
const PreviewStep: React.FC<PreviewStepProps> = ({
  generatedFleetData,
  fleetName,
  onFleetNameChange,
  onRegenerate,
  peakPeriods,
  mode = 'manual',
  interactive = false,
  editedSource = 'generated',
  onBlockClick,
  onResetEdits,
}) => {
  const { t } = useTranslation('mapWizards');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);

  // Track generation analytics
  useEffect(() => {
    if (generatedFleetData) {
      logEvent({
        category: 'AutoFleetGenerator',
        action: 'PreviewGenerated',
        label: fleetName,
        value: generatedFleetData.summary.totalVehicles,
        total_vehicles: generatedFleetData.summary.totalVehicles,
        total_shifts: generatedFleetData.summary.totalShifts,
        total_breaks: generatedFleetData.summary.totalBreaks,
      });
    }
  }, [generatedFleetData, fleetName]);

  const handleRegenerate = async () => {
    setIsGenerating(true);
    setGenerationError(null);

    try {
      logEvent({
        category: 'AutoFleetGenerator',
        action: 'RegenerateRequested',
        label: fleetName,
      });

      await onRegenerate();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('autoFleetGen.preview.regenerateFailed');
      setGenerationError(errorMessage);

      logEvent({
        category: 'AutoFleetGenerator',
        action: 'RegenerationFailed',
        label: fleetName,
        error: errorMessage,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Extract service start/end from summary
  const getServiceBounds = () => {
    if (!generatedFleetData) return { start: 0, end: 0 };
    
    const [start, end] = generatedFleetData.summary.servicePeriod.split(' - ');
    return {
      start: parseTime(start),
      end: parseTime(end),
    };
  };

  const serviceBounds = getServiceBounds();

  if (!generatedFleetData) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={400}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 3 }}>
          {t('autoFleetGen.preview.generating')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('autoFleetGen.preview.creatingOptimal')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Fleet Name Input */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
        <Typography variant="subtitle2" gutterBottom>
          {t('autoFleetGen.preview.fleetName')}
        </Typography>
        <TextField
          fullWidth
          value={fleetName}
          onChange={(e) => {
            onFleetNameChange(e.target.value);
            logEvent({
              category: 'AutoFleetGenerator',
              action: 'FleetNameChanged',
              label: e.target.value,
            });
          }}
          placeholder={t('autoFleetGen.preview.fleetNamePlaceholder')}
          helperText={t('autoFleetGen.preview.fleetNameHelper')}
        />
      </Paper>

      {/* Success Message */}
      <Alert
        severity="success"
        icon={<Icon size={20} name="circle-check" />}
        sx={{ mb: 3 }}
        action={
          <Button
            color="inherit"
            size="small"
            startIcon={isGenerating ? <CircularProgress size={16} /> : <Icon name="arrows-rotate" />}
            onClick={handleRegenerate}
            disabled={isGenerating}
          >
            {t('autoFleetGen.preview.regenerate')}
          </Button>
        }
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t('autoFleetGen.preview.successTitle')}
        </Typography>
        <Typography variant="caption">
          {t('autoFleetGen.preview.successDetail', { vehicles: generatedFleetData.summary.totalVehicles, shifts: generatedFleetData.summary.totalShifts })}
        </Typography>
      </Alert>

      {editedSource === 'edited' && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            onResetEdits ? (
              <Button color="inherit" size="small" onClick={onResetEdits}>
                {t('autoFleetGen.preview.resetTimelineEdits')}
              </Button>
            ) : undefined
          }
        >
          {t('autoFleetGen.preview.editedAlert')}
        </Alert>
      )}

      {/* Error Display */}
      {generationError && (
        <Alert severity="error" icon={<Icon size={20} name="circle-exclamation" />} sx={{ mb: 3 }} onClose={() => setGenerationError(null)}>
          <Typography variant="body2">{generationError}</Typography>
        </Alert>
      )}

      {/* Summary Cards */}
      <FleetSummaryCards 
        summary={generatedFleetData.summary} 
        mixedFleetSummary={generatedFleetData.mixedFleetSummary}
        mode={mode}
      />

      <Divider sx={{ my: 3 }} />

      {/* Timeline Visualization - Different component for fixed routes and mixed service */}
      {mode === 'fixed-routes' ? (
        <FixedRouteTimetableVisualization
          vehicles={generatedFleetData.vehicles}
          serviceStartMinutes={serviceBounds.start}
          serviceEndMinutes={serviceBounds.end}
          intervalMinutes={30} // 30-minute blocks for better granularity
          interactive={interactive}
          onBlockClick={onBlockClick}
        />
      ) : mode === 'mixed' ? (
        <MixedServiceTimetableVisualization
          vehicles={generatedFleetData.vehicles}
          serviceStartMinutes={serviceBounds.start}
          serviceEndMinutes={serviceBounds.end}
          intervalMinutes={30} // 30-minute blocks for better granularity
          interactive={interactive}
          onBlockClick={onBlockClick}
        />
      ) : (
        <FleetTimelineVisualization
          vehicles={generatedFleetData.shifts}
          serviceStartMinutes={serviceBounds.start}
          serviceEndMinutes={serviceBounds.end}
          intervalMinutes={30} // 30-minute blocks for better granularity
          peakPeriods={peakPeriods}
          interactive={interactive}
          onBlockClick={onBlockClick}
        />
      )}

      {interactive && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {t('autoFleetGen.preview.interactiveHint')}
        </Typography>
      )}

      {/* Additional Info */}
      <Paper sx={{ p: 2, mt: 3, bgcolor: '#f9f9f9' }}>
        <Typography variant="caption" color="text.secondary">
          <strong>{t('autoFleetGen.preview.noteLabel')}</strong> {t('autoFleetGen.preview.noteBody')}
        </Typography>
      </Paper>
    </Box>
  );
};

export default PreviewStep;

